import { createContext, useContext, useEffect, useState, useCallback } from "react";
import client, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [activeOrgId, setActiveOrgId] = useState(localStorage.getItem("pf_org") || null);
  const [loading, setLoading] = useState(true);

  const applyOrg = useCallback((id) => {
    setActiveOrgId(id);
    if (id) localStorage.setItem("pf_org", id);
    else localStorage.removeItem("pf_org");
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await client.get("/auth/me");
      setUser(data.user);
      setOrgs(data.organizations || []);
      const stored = localStorage.getItem("pf_org");
      const valid = (data.organizations || []).some((o) => o.id === stored);
      if (!valid && data.organizations?.length) applyOrg(data.organizations[0].id);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [applyOrg]);

  useEffect(() => {
    if (localStorage.getItem("pf_token")) loadMe();
    else { setUser(false); setLoading(false); }
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await client.post("/auth/login", { email, password });
    localStorage.setItem("pf_token", data.token);
    await loadMe();
    return data;
  };

  const register = async (payload) => {
    const { data } = await client.post("/auth/register", payload);
    localStorage.setItem("pf_token", data.token);
    if (data.organization_id) applyOrg(data.organization_id);
    await loadMe();
    return data;
  };

  const logout = async () => {
    try { await client.post("/auth/logout"); } catch {}
    localStorage.removeItem("pf_token");
    localStorage.removeItem("pf_org");
    setUser(false);
    setOrgs([]);
    setActiveOrgId(null);
  };

  const activeOrg = orgs.find((o) => o.id === activeOrgId) || orgs[0] || null;
  const role = activeOrg?.role || "viewer";

  return (
    <AuthContext.Provider
      value={{ user, orgs, activeOrg, activeOrgId: activeOrg?.id, role, loading,
               login, register, logout, setActiveOrg: applyOrg, reload: loadMe, formatApiErrorDetail }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
